import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { apiClient } from './services/api';
import { restoreSession, signOut } from './services/auth';
import { useSession } from './store/session';
import { offlineQueue } from './offline/queue';

import { PublicLayout } from './public/PublicLayout';
import { HomePage } from './public/HomePage';
import { AboutPage } from './public/AboutPage';
import { AcademyPage } from './public/AcademyPage';
import { LaboratoriesPage } from './public/LaboratoriesPage';
import { InstitutionsPage } from './public/InstitutionsPage';
import { MembershipPage } from './public/MembershipPage';
import { ContactPage } from './public/ContactPage';
import { VerifyPage } from './public/VerifyPage';

import { LoginPage } from './auth/LoginPage';
import { OnboardingPage } from './auth/OnboardingPage';
import { OrgSwitcherPage } from './org/OrgSwitcherPage';
import { OrgGate } from './org/OrgGate';
import { AppShell } from './shell/AppShell';
import { FullPageSpinner } from './shell/Spinner';

import { DashboardRouter } from './dashboards/DashboardRouter';
import { CatalogPage } from './catalog/CatalogPage';
import { CoursePage } from './catalog/CoursePage';
import { LessonPage } from './player/LessonPage';
import { LabsPage } from './labs/LabsPage';
import { MembersPage } from './admin/MembersPage';
import { BillingPage } from './billing/BillingPage';
import { CertificatesPage } from './certificates/CertificatesPage';
import { PacksPage } from './admin/PacksPage';

/**
 * The lab engine is a lazy chunk.
 *
 * Most sessions never open a lab, and the learners who do are on the worst
 * connections — so the plotter, the scene and the SVG chart code are loaded
 * when a lab is opened and not before. Splitting it here rather than only in
 * the rollup config means the dev server behaves the same way, which is how you
 * notice a regression before the build does.
 */
const LabPage = lazy(() => import('./labs/LabPage').then(m => ({ default: m.LabPage })));
const LabPreviewPage = lazy(() =>
  import('./labs/LabPreviewPage').then(m => ({ default: m.LabPreviewPage })),
);

export function App() {
  const navigate = useNavigate();
  const { ready, setReady, setSession, reset } = useSession();

  useEffect(() => {
    apiClient.setUnauthorizedHandler(() => {
      void signOut().finally(() => {
        reset();
        navigate('/login', { replace: true });
      });
    });
  }, [navigate, reset]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const hasIdentity = await restoreSession();
      if (cancelled) return;

      if (!hasIdentity) {
        setReady(true);
        return;
      }

      try {
        const session = await apiClient.session();
        if (cancelled) return;
        setSession(session.profile, session.orgs);
      } catch {
        // A stored token that the server will not accept is the same as no
        // token. Clearing it here stops a refresh loop between the shell and
        // the login page.
        apiClient.clear();
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setReady, setSession]);

  useEffect(() => offlineQueue.start(), []);

  return (
    <Routes>
      {/* Public. Prerendered at build time — see scripts/prerender.mjs. */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/academy" element={<AcademyPage />} />
        <Route path="/laboratories" element={<LaboratoriesPage />} />
        <Route path="/institutions" element={<InstitutionsPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/verify/:serial?" element={<VerifyPage />} />
      </Route>

      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/switch" element={<OrgSwitcherPage />} />

      {/*
        Every authenticated route is nested under `:orgSlug`, so the acting
        organisation is in the URL. Two consequences worth having: a teacher can
        bookmark "my class at St Mary's" and land in the right school, and
        switching schools is a navigation rather than a hidden mode.
      */}
      <Route
        // No splat: a parent with `/*` swallows its children in React Router 7,
        // and the nested routes below would never match.
        path="/o/:orgSlug"
        element={
          !ready ? (
            <FullPageSpinner />
          ) : (
            <OrgGate>{() => <AppShell />}</OrgGate>
          )
        }
      >
        <Route index element={<DashboardRouter />} />
        <Route path="courses" element={<CatalogPage />} />
        <Route path="courses/:slug" element={<CoursePage />} />
        <Route path="courses/:slug/lessons/:lessonId" element={<LessonPage />} />
        <Route path="labs" element={<LabsPage />} />
        <Route
          path="labs/:labSlug"
          element={
            <Suspense fallback={<FullPageSpinner label="Loading the laboratory…" />}>
              <LabPage />
            </Suspense>
          }
        />
        <Route
          path="labs/:labSlug/preview"
          element={
            <Suspense fallback={<FullPageSpinner label="Loading the preview harness…" />}>
              <LabPreviewPage />
            </Suspense>
          }
        />
        <Route path="members" element={<MembersPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="packs" element={<PacksPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    // Supabase puts the session in the URL fragment and its client picks it up
    // on load, so by the time this mounts there is nothing to parse — only a
    // decision about where to send them.
    void restoreSession().then(() => navigate('/switch', { replace: true }));
  }, [navigate]);
  return <FullPageSpinner label="Signing you in…" />;
}
